import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';
import { i18n } from '../../translate/i18n';
import { SocketContext } from '../Socket/SocketContext';
import usePermissions from '../../hooks/usePermissions';

const MetricsContext = createContext();

export const useMetricsContext = () => {
  const context = useContext(MetricsContext);
  if (!context) {
    throw new Error('useMetricsContext must be used within a MetricsProvider');
  }
  return context;
};

export const MetricsProvider = ({ children }) => {
  const [metrics, setMetrics] = useState(null);
  const [realtimeMetrics, setRealtimeMetrics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [realtimeLoading, setRealtimeLoading] = useState(false);
  const [filters, setFilters] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    userId: null,
    queueId: null,
    tagId: null,
    pipelineStage: null,
  });

  const socketManager = useContext(SocketContext);
  const { permissions } = usePermissions();

  const fetchMetrics = useCallback(
    async (customFilters = null) => {
      // Verificar se o usuário está logado
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('Usuário não está logado, pulando requisição de métricas');
        return;
      }

      if (!permissions) return; // Aguardar permissões carregarem

      setLoading(true);
      try {
        const params = customFilters || filters;
        const queryParams = new URLSearchParams();

        // Aplicar filtros baseados em permissões
        Object.entries(params).forEach(([key, value]) => {
          if (value !== null && value !== undefined && value !== '') {
            // Para usuários não-admin, forçar userId para o próprio usuário
            if (key === 'userId' && !permissions.isAdmin) {
              queryParams.append(key, permissions.userId);
            } else if (key === 'userId' && permissions.isAdmin) {
              // Admin pode filtrar por qualquer usuário
              queryParams.append(key, value);
            } else if (key !== 'userId') {
              // Outros filtros são aplicados normalmente
              queryParams.append(key, value);
            }
          }
        });

        // Se não for admin e não tiver userId definido, adicionar o próprio userId
        if (!permissions.isAdmin && !params.userId) {
          queryParams.append('userId', permissions.userId);
        }

        const { data } = await api.get(`/metrics?${queryParams.toString()}`);
        setMetrics(data);
      } catch (error) {
        console.error('Erro ao buscar métricas:', error);
        // Se a API não estiver implementada, usar dados mock
        if (error.response?.status === 500 || error.response?.status === 404) {
          const mockData = {
            conversionMetrics: {
              totalConversions: 0,
              averageConversionTime: 0,
              conversionRate: 0,
              totalValue: 0,
              goalProgress: {
                conversion: 0,
                financial: 0,
                leads: 0,
                messages: 0,
              },
            },
            leadsMetrics: {
              totalLeads: 0,
              newLeads: 0,
              qualifiedLeads: 0,
            },
            messageMetrics: {
              totalMessages: 0,
              sentMessages: 0,
              receivedMessages: 0,
              hourlyDistribution: Array.from({ length: 24 }, (_, i) => ({
                hour: i,
                sent: 0,
                received: 0,
              })),
            },
            performanceMetrics: {
              ticketsPerUser: [],
              queuePerformance: [],
            },
            ticketsByUser: [], // Novo campo unificado
            pendingTickets: 0, // ✅ Novo campo para tickets pendentes
            activeTickets: 0, // ✅ Novo campo para tickets ativos
            completedTickets: 0, // ✅ Novo campo para tickets concluídos
            averageResponseTime: 0, // ✅ Novo campo para tempo médio de resposta
          };
          setMetrics(mockData);
        } else {
          toast.error(i18n.t('metrics.errors.fetchError'));
        }
      } finally {
        setLoading(false);
      }
    },
    [filters, permissions]
  );

  const fetchRealtimeMetrics = useCallback(async () => {
    // Verificar se o usuário está logado
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn(
        'Usuário não está logado, pulando requisição de métricas em tempo real'
      );
      return;
    }

    setRealtimeLoading(true);
    try {
      const { data } = await api.get('/metrics/realtime');
      setRealtimeMetrics(data);
    } catch (error) {
      console.error('Erro ao buscar métricas em tempo real:', error);
      // Se a API não estiver implementada, usar dados mock
      if (error.response?.status === 500 || error.response?.status === 404) {
        const mockRealtimeData = {
          openTickets: 0,
          pendingTickets: 0,
          closedTickets: 0,
          onlineUsers: 0,
          totalMessages: 0,
          averageResponseTime: 0,
          averageTalkTime: 0,
          averageWaitTime: 0,
          conversionRate: 0,
          newContacts: 0,
        };
        setRealtimeMetrics(mockRealtimeData);
      } else {
        toast.error(i18n.t('metrics.errors.fetchRealtimeError'));
      }
    } finally {
      setRealtimeLoading(false);
    }
  }, []);

  const updateFilters = useCallback(newFilters => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  }, []);

  const resetFilters = useCallback(() => {
    setFilters({
      startDate: new Date().toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0],
      userId: null,
      queueId: null,
      tagId: null,
      pipelineStage: null,
    });
  }, []);

  // WebSocket para atualizações em tempo real
  useEffect(() => {
    let isMounted = true;

    // Verificar se o usuário está logado
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn(
        'Usuário não está logado, não configurando WebSocket para métricas'
      );
      return;
    }

    const companyId = localStorage.getItem('companyId');
    const socket = socketManager.getSocket(companyId);

    socket.on('metrics-update', data => {
      if (isMounted) {
        setRealtimeMetrics(prev => ({
          ...prev,
          ...data,
        }));
      }
    });

    socket.on('metrics-realtime-update', data => {
      if (isMounted) {
        setRealtimeMetrics(data);
      }
    });

    return () => {
      isMounted = false;
      socket.disconnect();
    };
  }, [socketManager]);

  useEffect(() => {
    let isMounted = true;

    const loadMetrics = async () => {
      // Verificar se o usuário está logado
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('Usuário não está logado, não carregando métricas');
        return;
      }

      if (isMounted) {
        await fetchMetrics();
        await fetchRealtimeMetrics();
      }
    };

    loadMetrics();

    // Atualizar métricas em tempo real a cada 30 segundos
    const interval = setInterval(() => {
      const token = localStorage.getItem('token');
      if (isMounted && token) {
        fetchRealtimeMetrics();
      }
    }, 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [fetchMetrics, fetchRealtimeMetrics]);

  const value = {
    metrics,
    realtimeMetrics,
    loading,
    realtimeLoading,
    filters,
    fetchMetrics,
    fetchRealtimeMetrics,
    updateFilters,
    resetFilters,
  };

  return (
    <MetricsContext.Provider value={value}>{children}</MetricsContext.Provider>
  );
};
